import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Field, TextInput } from '@l2u/ui/components/Field.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { AuthSplitLayout } from '@l2u/ui/components/AuthSplitLayout.tsx';
import { getAdminSupabase } from '@l2u/ui/lib/supabase.ts';

export function ForgotPassword() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const supabase = getAdminSupabase();
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo,
      });
      if (resetErr) throw resetErr;
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthSplitLayout
      variant="admin"
      formFooter={
        <Link to="/login" className="font-semibold text-brand-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">Forgot your password?</h2>
          <p className="text-sm text-slate-500">
            {sent
              ? 'Check your inbox for a reset link.'
              : 'Enter your email and we’ll send a reset link.'}
          </p>
        </div>

        {sent ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
            If an admin account exists for <span className="font-medium">{email.trim()}</span>,
            we’ve emailed a link to reset your password. Check your inbox (and spam folder).
          </div>
        ) : (
          <form className="flex flex-col gap-5" onSubmit={onSubmit}>
            <Field label="Email" required>
              <TextInput
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                style={{ borderRadius: 10 }}
              />
            </Field>
            <ErrorMessage error={error} />
            <Btn
              type="submit"
              loading={submitting}
              disabled={!email.trim()}
              className="w-full"
              style={{ borderRadius: 10, height: 48 }}
            >
              Send reset link
            </Btn>
          </form>
        )}
      </div>
    </AuthSplitLayout>
  );
}
